import { useMemo } from 'react';
import { Link } from '@remix-run/react';
import type { Dayjs } from 'dayjs';
import { DownloadIcon, FileIcon, InfoIcon, Loader2Icon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { RICH_TEXT_COMPACT_CLASS } from '~/utils/rich-text';
import { localizeTime } from '~/modules/root';
import { useUser } from '~/modules/user';
import { isImageFile } from '~/modules/file/util/is-image-file';
import type { Message } from '../../types';
import { formatDateFull, formatDatePartial, linkifyText } from '../../utils';
import { ParticipantAvatar } from '../ParticipantAvatar';

const TOOLTIP_DELAY_DURATION = 300;

/**
 * A message bubble representing a message in a conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatBoxMessageBubble({ message }: { message: Message }) {
    const user = useUser();
    const sender = message.Participant.Identity;
    const isOutgoing = useMemo(() => {
        return sender.__typename === 'Member' && sender.ID === user.ID;
    }, [sender, user.ID]);
    const senderName = useMemo(() => {
        if (isOutgoing) {
            return 'You';
        } else if (message.CreatedBy) {
            return message.CreatedBy;
        } else if (sender.__typename === 'Professional') {
            return sender.Title;
        }
        return sender.Name;
    }, [isOutgoing, message, sender]);

    return (
        <div className="flex items-start gap-[8px]">
            <ParticipantAvatar
                className="mt-[4px] size-[32px]"
                fallbackClassName="!text-[14px]"
                participant={sender}
            />
            <div className="min-w-0 flex-1">
                {message.__typename === 'TextMessage' && (
                    <TextMessageBubble
                        content={message.Content}
                        sender={senderName}
                        isHTML={message.IsHTML}
                        createdAt={localizeTime(message.Created)}
                        isOptimistic={message.isOptimistic ?? false}
                    />
                )}
                {message.__typename === 'FileAttachmentMessage' && (
                    <FileAttachmentMessageBubble
                        content={message.Content}
                        attachments={message.Attachments}
                        sender={senderName}
                        isHTML={message.IsHTML}
                        isOutgoing={isOutgoing}
                        createdAt={localizeTime(message.Created)}
                        isOptimistic={message.isOptimistic ?? false}
                    />
                )}
                {message.__typename === 'MemberEnquiryMessage' && (
                    <MemberEnquiryMessageBubble
                        message={message}
                        sender={senderName}
                        isOutgoing={isOutgoing}
                    />
                )}
                {message.__typename === 'RevealContactDetailsMessage' && (
                    <RevealContactDetailsMessageBubble message={message} />
                )}
            </div>
        </div>
    );
}

function TextMessageBubble({
    content,
    sender,
    isHTML,
    createdAt,
    isOptimistic,
}: {
    content: string;
    sender: string;
    isHTML: boolean;
    createdAt: Dayjs;
    isOptimistic: boolean;
}) {
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const fullDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);
    const linkifiedContent = useMemo(() => {
        return isHTML
            ? [content]
            : linkifyText(content, 'text-black underline font-medium');
    }, [content, isHTML]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="w-fit min-w-[40%] max-w-full space-y-[2px] break-words rounded-lg bg-white p-[8px] text-[12px]">
                        <div className="font-medium">{sender}:</div>
                        {isHTML ? (
                            <div
                                className={cn(
                                    RICH_TEXT_COMPACT_CLASS,
                                    'whitespace-pre-wrap'
                                )}
                                dangerouslySetInnerHTML={{ __html: content }}
                            ></div>
                        ) : (
                            <div className="whitespace-pre-wrap">
                                {linkifiedContent}
                            </div>
                        )}
                        <div className="flex items-center justify-end gap-[4px] text-[10px] text-foreground/70">
                            <time
                                className="flex-1 truncate text-right"
                                dateTime={a11yDateString}
                            >
                                {createdAtString}
                            </time>
                            {isOptimistic && (
                                <Loader2Icon className="size-[12px] shrink-0 animate-spin" />
                            )}
                        </div>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right">{fullDateString}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}

function FileAttachmentMessageBubble({
    content,
    attachments,
    sender,
    isHTML,
    isOutgoing,
    createdAt,
    isOptimistic,
}: {
    content?: string | null;
    attachments: Extract<
        Message,
        { __typename: 'FileAttachmentMessage' }
    >['Attachments'];
    sender: string;
    isHTML: boolean;
    isOutgoing: boolean;
    createdAt: Dayjs;
    isOptimistic: boolean;
}) {
    const fullDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);

    return (
        <div className="space-y-[8px]">
            {!!content && (
                <TextMessageBubble
                    content={content}
                    sender={sender}
                    isHTML={isHTML}
                    createdAt={createdAt}
                    isOptimistic={isOptimistic}
                />
            )}
            {!!attachments.length && (
                <div
                    className={cn(
                        'flex gap-[8px]',
                        isOutgoing ? 'flex-wrap' : 'flex-col'
                    )}
                >
                    {attachments.map(({ ID, Link, FileName }) => (
                        <TooltipProvider
                            key={ID}
                            delayDuration={TOOLTIP_DELAY_DURATION}
                        >
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className={cn(
                                            'relative overflow-hidden rounded-lg bg-white',
                                            !isOutgoing && 'max-w-[60%]'
                                        )}
                                    >
                                        <a
                                            className="flex size-full items-center gap-[8px] p-[8px] hover:bg-gray-200"
                                            href={Link}
                                            target="_blank"
                                            rel="noopener"
                                            download
                                        >
                                            {!isOutgoing &&
                                            isImageFile(FileName) ? (
                                                <img
                                                    className="size-[48px] shrink-0 object-cover"
                                                    src={Link}
                                                    alt={FileName ?? ''}
                                                />
                                            ) : (
                                                <FileIcon
                                                    className={cn(
                                                        'shrink-0',
                                                        isOutgoing
                                                            ? 'size-[24px]'
                                                            : 'size-[48px] p-[12px]'
                                                    )}
                                                />
                                            )}
                                            <div className="text-[12px]">
                                                <div className="line-clamp-2 break-words font-medium">
                                                    {FileName}
                                                </div>
                                                {!isOutgoing && (
                                                    <div className="flex items-center gap-[4px] text-black/70">
                                                        <DownloadIcon className="size-[12px]" />
                                                        <span>Download</span>
                                                    </div>
                                                )}
                                            </div>
                                        </a>
                                        {(isOptimistic || !Link) &&
                                            isOutgoing && (
                                                <div className="absolute inset-0 flex items-center justify-center bg-white/80">
                                                    <Loader2Icon className="size-[20px] animate-spin" />
                                                </div>
                                            )}
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    {fullDateString}
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ))}
                </div>
            )}
        </div>
    );
}

function MemberEnquiryMessageBubble({
    message,
    sender,
    isOutgoing,
}: {
    message: Extract<Message, { __typename: 'MemberEnquiryMessage' }>;
    sender: string;
    isOutgoing: boolean;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const fullDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const items = useMemo(() => {
        return message.LibraryItems.filter(
            (item) => item.__typename !== 'Professional'
        );
    }, [message.LibraryItems]);

    // TODO: Tracker: CommentLibraryItems.tsx

    return (
        <div className="space-y-[8px]">
            {items.map(
                ({ ID, Title, Link: link, Professional, LibraryThumb }) => (
                    <TooltipProvider
                        key={ID}
                        delayDuration={TOOLTIP_DELAY_DURATION}
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Link
                                    className="flex w-fit min-w-[60%] max-w-full items-center gap-[8px] rounded-lg bg-white p-[8px] hover:bg-gray-200"
                                    to={link}
                                >
                                    {!!LibraryThumb?.ThumbImageFile
                                        ?.PhotoEnquiryThumb && (
                                        <Image
                                            className="size-[48px] shrink-0"
                                            src={
                                                LibraryThumb.ThumbImageFile
                                                    .PhotoEnquiryThumb
                                            }
                                            alt={Title}
                                            fit="contain"
                                            fadeIn={false}
                                        />
                                    )}
                                    <div className="flex-1 space-y-[2px] overflow-hidden text-[12px]">
                                        <div className="truncate">
                                            {Professional?.Title}
                                        </div>
                                        <div className="line-clamp-2 break-words font-medium">
                                            {Title}
                                        </div>
                                    </div>
                                </Link>
                            </TooltipTrigger>
                            <TooltipContent side="right">
                                {fullDateString}
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )
            )}
            {message.Attachments.length ? (
                <FileAttachmentMessageBubble
                    content={message.Content}
                    attachments={message.Attachments}
                    sender={sender}
                    isHTML
                    isOutgoing={isOutgoing}
                    createdAt={createdAt}
                    isOptimistic={message.isOptimistic ?? false}
                />
            ) : (
                <TextMessageBubble
                    content={message.Content}
                    sender={sender}
                    isHTML
                    createdAt={createdAt}
                    isOptimistic={message.isOptimistic ?? false}
                />
            )}
        </div>
    );
}

function RevealContactDetailsMessageBubble({
    message,
}: {
    message: Extract<Message, { __typename: 'RevealContactDetailsMessage' }>;
}) {
    const createdAt = useMemo(() => {
        return localizeTime(message.Created);
    }, [message.Created]);
    const createdAtString = useMemo(() => {
        return formatDatePartial(createdAt);
    }, [createdAt]);
    const fullDateString = useMemo(() => {
        return formatDateFull(createdAt);
    }, [createdAt]);
    const a11yDateString = useMemo(() => {
        return createdAt.format('YYYY-MM-DD HH:mm');
    }, [createdAt]);

    return (
        <TooltipProvider delayDuration={TOOLTIP_DELAY_DURATION}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div className="space-y-[2px] rounded-lg border border-gray-200 bg-white p-[8px]">
                        <div className="flex items-center gap-[8px] break-words">
                            <InfoIcon className="size-[16px]" />
                            <div className="min-w-0 flex-1 text-[12px]">
                                {message.Content}
                            </div>
                        </div>
                        <time
                            className="block truncate text-right text-[10px] text-foreground/70"
                            dateTime={a11yDateString}
                        >
                            {createdAtString}
                        </time>
                    </div>
                </TooltipTrigger>
                <TooltipContent side="right">{fullDateString}</TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
}
